import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { useNavigate } from "react-router-dom";
import {
  smartTrim, 
  trimOnBlur,
  UserAddDateToMMDDYYYY,
} from "../../utils/Utils";
import { ApplicationApi } from "../../service/Api/ApplicationsApi";
import { useAuth } from "../../context/auth-context";
import { boolean } from "yup";

const EMPTY_SEARCH_CRITERIA = {
  firstName: "",
  lastName: "",
  username: "",
  email: "",
  isActive: "",
};

const UserSearch = () => {
  const { userRole } = useAuth();
  const hasInitialFetched = useRef(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [localPageSize, setLocalPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [isPageSizeChange, setIsPageSizeChange] = useState(boolean);
  const [searchCriteria, setSearchCriteria] = useState(EMPTY_SEARCH_CRITERIA);
  // Last criteria applied to the table — keeps Search/Clear usable after inputs are emptied
  const [appliedCriteria, setAppliedCriteria] = useState(EMPTY_SEARCH_CRITERIA);
  const columns = useMemo(
    () => [
      {
        Header: "First Name",
        accessor: "firstName",
      },
      {
        Header: "Last Name",
        accessor: "lastName",
      },
      {
        Header: "Username",
        accessor: "username",
        Cell: ({ row }) => (
          <span
            className="cursor-pointer primary text-decoration-underline text-capitalize"
            onClick={() => handleRowClick(row.original.userID_PK)} // Assuming 'id' is the unique identifier
          >
            {row.values.username}
          </span>
        ),
      },
      {
        Header: "User Role",
        accessor: "roleName",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Created Date",
        accessor: "addDate",
        Cell: ({ value }) =>
          value ? UserAddDateToMMDDYYYY(value) : "No date provided",
      },
      {
        Header: "Is Active",
        accessor: "isActive",
        Cell: ({ value }) => (value === "True" ? "Yes" : "No"),
      },
      //{
      //  Header: "Email Notification",
      //  accessor: "isCommunicationActive",
      //  Cell: ({ value }) => (value === true ? "Yes" : "No"),
      //},
    ],
    [],
  );
  const navigate = useNavigate();
  const handleRowClick = (userId) => {
    navigate(`/UserDetail/${userId}`);
  };

  const handleCreateNewUser = () => {
    navigate("/UserDetail");
  };

  const handleLinkUser = () => {
    navigate("/LinkUser");
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: currentPage, sortBy: [] },
      manualPagination: true,
      manualSortBy: true,
      pageCount: pageCount,
    },
    useSortBy,
    usePagination,
  );

  const loadUsers = useCallback(
    async ({
      firstName = "",
      lastName = "",
      username = "",
      email = "",
      isActive = "",
      pageNumber = 1,
      pageSize = localPageSize,
    } = {}) => {
      if (userRole == null) {
        return;
      }

      setLoading(true);
      try {
        const response = await ApplicationApi.getPortalUserList(
          firstName,
          lastName,
          username,
          email,
          isActive,
          pageNumber,
          pageSize,
          userRole,
        );

        setData(response?.items || []);
        setTotalRecords(response?.totalCount || 0);
        setPageCount(response?.totalPages || 0);
        setCurrentPage(response?.currentPage || response?.pageNumber || pageNumber);
        setHasNextPage(response?.hasNextPage ?? false);
        setHasPreviousPage(response?.hasPreviousPage ?? false);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    },
    [userRole, localPageSize],
  );

  useEffect(() => {
    if (userRole == null || hasInitialFetched.current) {
      return;
    }
    hasInitialFetched.current = true;
    loadUsers({ pageNumber: 1, pageSize: localPageSize });
  }, [userRole, localPageSize, loadUsers]);

  const hasAnyCriteria = (criteria) =>
    Boolean(
      criteria.firstName ||
        criteria.lastName ||
        criteria.username ||
        criteria.email ||
        criteria.isActive,
    );

  const hasFormCriteria = hasAnyCriteria(searchCriteria);
  const hasAppliedCriteria = hasAnyCriteria(appliedCriteria);

  const handleClear = () => {
    setSearchCriteria(EMPTY_SEARCH_CRITERIA);
    setAppliedCriteria(EMPTY_SEARCH_CRITERIA);
    setLocalPageSize(10);
    setCurrentPage(1);
    loadUsers({ pageNumber: 1, pageSize: 10 });
  };

  const cleanString = (value) =>
    typeof value === "string" ? value.trim().replace(/\s+/g, " ") : value;

  // When the user empties the last search field, reload all data (same as Clear)
  const handleCriteriaChange = (field, value) => {
    const nextCriteria = {
      ...searchCriteria,
      [field]: value,
    };
    setSearchCriteria(nextCriteria);

    if (!hasAnyCriteria(nextCriteria) && hasAppliedCriteria) {
      setAppliedCriteria(EMPTY_SEARCH_CRITERIA);
      setLocalPageSize(10);
      setCurrentPage(1);
      loadUsers({ pageNumber: 1, pageSize: 10 });
    }
  };

  const handleSearchSubmit = async (overrideCriteria) => {
    const source = overrideCriteria || searchCriteria;
    const nextCriteria = {
      firstName: cleanString(source.firstName || ""),
      lastName: cleanString(source.lastName || ""),
      username: cleanString(source.username || ""),
      email: cleanString(source.email || ""),
      isActive: source.isActive || "",
    };

    setSearchCriteria(nextCriteria);
    setAppliedCriteria(nextCriteria);
    setCurrentPage(1);
    await loadUsers({
      ...nextCriteria,
      pageNumber: 1,
      pageSize: localPageSize,
    });
  };

  const handleChangePageSize = async (newPageSize) => {
    setIsPageSizeChange(true);
    setLocalPageSize(newPageSize);

    const newTotalPages = Math.ceil(totalRecords / newPageSize) || 1;
    let newCurrentPage =
      currentPage >= newTotalPages ? newTotalPages : currentPage;
    if (isPageSizeChange) {
      newCurrentPage = 1;
    }

    setCurrentPage(newCurrentPage);
    await loadUsers({
      ...appliedCriteria,
      pageNumber: newCurrentPage,
      pageSize: newPageSize,
    });
  };

  const handleNext = async () => {
    if (hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      await loadUsers({
        ...appliedCriteria,
        pageNumber: nextPage,
        pageSize: localPageSize,
      });
    }
  };

  const handlePrevious = async () => {
    const prevPage = currentPage - 1;
    setCurrentPage(prevPage);
    await loadUsers({
      ...appliedCriteria,
      pageNumber: prevPage,
      pageSize: localPageSize,
    });
  };

  const handleGoToFirstPage = async () => {
    setCurrentPage(1);
    await loadUsers({
      ...appliedCriteria,
      pageNumber: 1,
      pageSize: localPageSize,
    });
  };

  const handleGoToLastPage = async () => {
    setCurrentPage(pageCount);
    await loadUsers({
      ...appliedCriteria,
      pageNumber: pageCount,
      pageSize: localPageSize,
    });
  };
  return (
    <>
      <div className="main-content">
        <div className="content-header d-flex justify-content-between align-items-center">
          <h1 className="page-header">User Search</h1>
          <div className="d-flex align-items-center gap-2">
            {Number(userRole) === 3 && (
              <button
                type="button"
                className="btn btn-border-primary h-48 d-flex align-items-center"
                onClick={handleLinkUser}
              >
                <i className="fh_link icon-mr fs-5"></i>
                <span>Link User</span>
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary h-48 d-flex align-items-center"
              onClick={handleCreateNewUser}
            >
              <i className="fh_add icon-mr fs-5"></i>
              <span>Create User</span>
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="card-form form-content mt-0">
              <div className="row">
                <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                  <label htmlFor="FirstName" className="d-flex form-label">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="FirstName"
                    className="form-text form-control"
                    value={searchCriteria.firstName}
                    onChange={(e) =>
                      handleCriteriaChange("firstName", smartTrim(e.target.value))
                    }
                    placeholder="Enter First Name"
                    onBlur={(e) =>
                      handleCriteriaChange("firstName", trimOnBlur(e.target.value))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit({
                          ...searchCriteria,
                          firstName: trimOnBlur(e.target.value),
                        });
                      }
                    }}
                  />
                </div>
                <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                  <label htmlFor="LastName" className="d-flex form-label">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="LastName"
                    className="form-text form-control"
                    value={searchCriteria.lastName}
                    onChange={(e) =>
                      handleCriteriaChange("lastName", smartTrim(e.target.value))
                    }
                    placeholder="Enter Last Name"
                    onBlur={(e) =>
                      handleCriteriaChange("lastName", trimOnBlur(e.target.value))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit({
                          ...searchCriteria,
                          lastName: trimOnBlur(e.target.value),
                        });
                      }
                    }}
                  />
                </div>
                <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                  <label htmlFor="Username" className="d-flex form-label">
                    Username
                  </label>
                  <input
                    type="text"
                    name="Username"
                    className="form-text form-control"
                    value={searchCriteria.username}
                    onChange={(e) =>
                      handleCriteriaChange("username", smartTrim(e.target.value))
                    }
                    onBlur={(e) =>
                      handleCriteriaChange("username", trimOnBlur(e.target.value))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit({
                          ...searchCriteria,
                          username: trimOnBlur(e.target.value),
                        });
                      }
                    }}
                    placeholder="Enter Username"
                  />
                </div>
                <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                  <label htmlFor="Email" className="d-flex form-label">
                    Email
                  </label>
                  <input
                    type="text"
                    name="Email"
                    className="form-text form-control"
                    value={searchCriteria.email}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchSubmit({
                          ...searchCriteria,
                          email: trimOnBlur(e.target.value),
                        });
                      }
                    }}
                    onChange={(e) =>
                      handleCriteriaChange("email", smartTrim(e.target.value))
                    }
                    placeholder="Enter Email"
                  />
                </div>
                <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4">
                  <label htmlFor="IsActive" className="d-flex form-label">
                    Is Active
                  </label>
                  <div className="form-field position-relative">
                    <select
                      name="IsActive"
                      className="form-select form-control"
                      value={searchCriteria.isActive}
                      onChange={(e) => {
                        handleCriteriaChange("isActive", e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit({
                            ...searchCriteria,
                            isActive: e.target.value,
                          });
                        }
                      }}
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                    <span className="field-icon position-absolute top-50 translate-middle-y">
                      <i className="fh_arrow_down_line"></i>
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-12 text-center">
                <button
                  type="button"
                  className="btn btn-primary h-48 me-3"
                  onClick={() => handleSearchSubmit()}
                  disabled={!hasFormCriteria && !hasAppliedCriteria}
                >
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-border-primary h-48"
                  onClick={handleClear}
                  disabled={!hasFormCriteria && !hasAppliedCriteria}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="card-content">
              <div className="content-subheader d-flex align-items-center">
                <h2 className="page-subheader me-3">User List </h2>
                <span className="total-record">
                  [ Total Users: {totalRecords} ]
                </span>
              </div>
              <div className="table-responsive">
                <table {...getTableProps()} className="table caption-top">
                  <thead>
                    {headerGroups.length > 0 &&
                      headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column) => (
                            <th>{column.render("Header")}</th>
                          ))}
                        </tr>
                      ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="text-center my-4"
                        >
                          <div className="spinner-border" role="status"></div>
                        </td>
                      </tr>
                    ) : data.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} className="text-center">
                          No data found
                        </td>
                      </tr>
                    ) : (
                      page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => (
                              <td {...cell.getCellProps()}>
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {data && data.length > 0 && (
              <div className="d-flex justify-content-center">
                <nav
                  aria-label="Page navigation"
                  className="d-flex align-items-center"
                >
                  <span className="pagnination-info me-2">
                    Page{" "}
                    <strong>
                      {currentPage ? currentPage : 1} of {pageCount}
                    </strong>
                  </span>
                  <ul className="pagination d-flex justify-content-end mb-0">
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={handleGoToFirstPage}
                        disabled={!hasPreviousPage}
                      >
                        <i className="fh_double_arrow_left"></i>
                      </button>
                    </li>
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={handlePrevious}
                        disabled={!hasPreviousPage}
                      >
                        <i className="fh_arrow_left_line"></i>
                      </button>
                    </li>
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={handleNext}
                        disabled={!hasNextPage}
                      >
                        <i className="fh_arrow_right_line"></i>
                      </button>
                    </li>
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={handleGoToLastPage}
                        disabled={!hasNextPage}
                      >
                        <i className="fh_double_arrow_right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
                <div>
                  <select
                    className="form-select form-control cursor-pointer custom-table-dropdown"
                    value={localPageSize}
                    onChange={(e) => {
                      handleChangePageSize(Number(e.target.value));
                    }}
                  >
                    {[10, 20, 30].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        Show {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default UserSearch;
